import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@carrent/ui'
import { kbApi } from '@carrent/api'
import { DiffView } from './DiffView'

/**
 * Правки на проверке.
 *
 * Слева список ожидающих, справа — что именно предлагают изменить.
 * Решение принимается по diff'у, а не по тексту целиком: проверяющий
 * должен видеть правку, а не перечитывать регламент.
 *
 * Конфликт (черновик писался от версии, которую уже заменили) виден
 * до решения — иначе аппрув молча откатил бы чужую публикацию.
 */
export function ReviewQueue({ onOpenPage }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeId, setActiveId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    kbApi.reviewQueue()
      .then((r) => {
        const list = r.data?.data || []
        setItems(list)
        setActiveId((cur) => (list.some((i) => i.id === cur) ? cur : list[0]?.id ?? null))
        setError('')
      })
      .catch((e) => setError(e.response?.data?.message || 'Не удалось загрузить очередь'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const active = items.find((i) => i.id === activeId) || null

  useEffect(() => {
    if (!active) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    setComment('')
    setActionError('')
    Promise.all([
      kbApi.revision(active.page_id, active.id),
      kbApi.page(active.page_id).catch(() => null),
    ])
      .then(([rev, page]) => {
        setDetail({
          revision: rev.data?.data || null,
          currentBody: page?.data?.data?.body_md || '',
        })
      })
      .catch((e) => setActionError(e.response?.data?.message || 'Не удалось открыть правку'))
      .finally(() => setDetailLoading(false))
  }, [active])

  const decide = async (decision) => {
    if (decision === 'reject' && !comment.trim()) {
      setActionError('Напишите, что исправить — автор увидит этот текст')
      return
    }
    setBusy(true)
    setActionError('')
    try {
      await kbApi.review(active.page_id, active.id, decision, comment.trim())
      load()
    } catch (e) {
      setActionError(e.response?.data?.message || 'Решение не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div className="kb__tree-loading"><div className="spinner" /></div>
  }

  if (error) {
    return <p className="kb__error">{error}</p>
  }

  if (!items.length) {
    return (
      <div className="kb-state kb-state--inline">
        <Icon name="check" size={28} aria-hidden="true" />
        <p className="kb-state__title">Проверять нечего</p>
        <p className="kb-state__hint">
          Здесь появятся правки, которые предложили редакторы ваших пространств.
        </p>
      </div>
    )
  }

  return (
    <div className="review">
      <ul className="review__list">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={`review__item${item.id === activeId ? ' is-active' : ''}`}
              onClick={() => setActiveId(item.id)}
            >
              <span className="review__item-title">{item.page_title}</span>
              <span className="review__item-author">{item.author_name || 'Автор неизвестен'}</span>
              {item.conflict && (
                <span className="review__conflict-flag">конфликт версий</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <div className="review__detail">
        {detailLoading && <div className="kb__tree-loading"><div className="spinner" /></div>}

        {active && detail && !detailLoading && (
          <>
            <header className="review__head">
              <div>
                <h2 className="review__title">{active.page_title}</h2>
                <p className="review__by">
                  Предложил {active.author_name || 'неизвестный автор'}
                </p>
              </div>
              <button type="button" className="btn btn--ghost btn--sm"
                onClick={() => onOpenPage(active)}>
                Открыть статью
              </button>
            </header>

            {active.conflict && (
              <div className="review__conflict">
                <Icon name="warning" size={16} aria-hidden="true" />
                <div>
                  <strong>Статью изменили, пока правка ждала проверки.</strong>
                  <p>
                    Правка писалась от предыдущей версии. Если её опубликовать,
                    более свежие изменения будут потеряны — сравните тексты ниже.
                  </p>
                </div>
              </div>
            )}

            <DiffView
              before={detail.currentBody}
              after={detail.revision?.body_md || ''}
              beforeLabel="Сейчас опубликовано"
              afterLabel="Предлагают"
            />

            <div className="review__decision">
              <label className="field">
                <span className="field__label">Что исправить (для отклонения)</span>
                <textarea
                  className="input review__comment"
                  value={comment}
                  rows={2}
                  placeholder="Уточните периодичность мойки зимой"
                  onChange={(e) => setComment(e.target.value)}
                />
              </label>
              {actionError && <p className="field__error">{actionError}</p>}
              <div className="review__actions">
                <button type="button" className="btn btn--ghost" disabled={busy}
                  onClick={() => decide('reject')}>
                  Вернуть на доработку
                </button>
                <button type="button" className="btn btn--primary" disabled={busy}
                  onClick={() => decide('approve')}>
                  Опубликовать
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
